import { useContext } from 'react';
import { TCalculateData } from 'pages/dimensions/store/dimensions';
import { TProjectConfigurationParameter } from 'pages/common/types';
import { DataContext } from 'entries/common/widgets/saved-project-configuration/context';
import { getProductsWithRequiredData } from 'pages/common/helpers/get-products-with-required-data';
import { sortComplimentaryProductsByPrice } from 'pages/common/helpers/sort-complimentary-products-by-price';

type TUpdateDataParams = {
  calculateParams: TCalculateData;
  updatedConfigurationParams: TProjectConfigurationParameter[];
};

export const useUpdateDataContext = () => {
  const { setData } = useContext(DataContext);

  const updateDataContext = ({ calculateParams, updatedConfigurationParams }: TUpdateDataParams) => {
    const complimentaryGroups = calculateParams.complimentaryGroups
      .map(group => ({
        ...group,
        products: getProductsWithRequiredData(group.products),
      }))
      .filter(group => group.products?.length > 0)
      .map(({ groupName, requiredToBuy, products }) => ({
        groupName,
        requiredToBuy,
        products: products.map(product => ({
          code: product.productId,
          type: 'PRODUCT',
          name: product?.displayedName?.value?.[0] ?? '',
          quantity: product.total?.requiredQuantity ?? 0,
          price: product.salesPriceValueInConsumerUnitValue ?? 0,
          complementary: true,
          image: product?.mediaMainPhoto?.value?.[0],
          productPageName: product.product_page_name?.value?.[0],
        })),
        variants: sortComplimentaryProductsByPrice(products).map(product => ({
          productId: product.productId,
          quantity: product.total?.requiredQuantity ?? 0,
          price: product.salesPriceValueInConsumerUnitValue ?? 0,
          onePiece: product.total?.onePiece ?? '',
        })),
      }));

    setData(prevData => ({
      ...prevData,
      projectConfigurationParameters: updatedConfigurationParams,
      generalItems: [
        {
          code: calculateParams.items[0].productId,
          type: 'PRODUCT',
          name: calculateParams?.items?.[0]?.displayedName?.value?.[0] ?? '',
          quantity: calculateParams.items[0].total.requiredQuantity,
          price: calculateParams.items[0].salesPriceValueInConsumerUnitValue ?? 0,
          complementary: false,
          image: calculateParams?.items?.[0]?.mediaMainPhoto?.value?.[0],
          productPageName: calculateParams?.items?.[0]?.productPageName?.value?.[0],
        },
        ...(getProductsWithRequiredData(calculateParams.complimentaryProductInfo)?.map(product => ({
          code: product.productId,
          type: 'PRODUCT',
          name: product?.displayedName?.value?.[0] ?? '',
          quantity: product.total?.requiredQuantity ?? 0,
          price: product.salesPriceValueInConsumerUnitValue ?? 0,
          complementary: true,
          image: product?.mediaMainPhoto?.value?.[0],
          productPageName: product.product_page_name?.value?.[0],
        })) ?? []),
      ],
      complimentaryGroups,
      services: calculateParams.services ?? [],
    }));
  };

  return { updateDataContext };
};
